import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './ImageCropper.css';

/**
 * ImageCropper Component
 * @param {string} imageSrc - The source image URL/base64 to crop
 * @param {function} onCropComplete - Callback with the final cropped blob
 * @param {function} onCancel - Callback to cancel cropping
 */
const ImageCropper = ({ imageSrc, onCropComplete, onCancel }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const onCropChange = useCallback((crop) => {
        setCrop(crop);
    }, []);

    const onZoomChange = useCallback((zoom) => {
        setZoom(zoom);
    }, []);

    const onCropCompleteEvent = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.setAttribute('crossOrigin', 'anonymous');
            image.src = url;
        });

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set fixed high resolution output (e.g. 2000x2000 for standard)
        // Or scaling relatively. User requested "auto resize".
        // Let's maximize quality.

        // We want output to be 2000x2000 square as per standards
        canvas.width = 2000;
        canvas.height = 2000;

        // Draw the cropped area onto the 2000x2000 canvas
        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            2000,
            2000
        );

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(blob);
            }, 'image/webp', 0.90); // WebP format at 90% quality
        });
    };

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        setIsProcessing(true);
        try {
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            onCropComplete(croppedImageBlob);
        } catch (e) {
            console.error(e);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="crop-container-overlay">
            <div className="crop-container">
                <div className="crop-header">
                    <h3>Adjust Image</h3>
                    <p>Drag to position • Scroll to zoom</p>
                </div>
                <div className="crop-area">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1} // Force 1:1 Aspect Ratio
                        onCropChange={onCropChange}
                        onCropComplete={onCropCompleteEvent}
                        onZoomChange={onZoomChange}
                        showGrid={true}
                    />
                </div>
                <div className="crop-controls">
                    <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        onChange={(e) => setZoom(e.target.value)}
                        className="zoom-range"
                    />
                    <div className="crop-actions">
                        <button className="btn-cancel" onClick={onCancel} disabled={isProcessing}>Cancel</button>
                        <button className="btn-confirm" onClick={handleSave} disabled={isProcessing}>
                            {isProcessing ? 'Processing...' : 'Done & Resize'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageCropper;
